// WebSocket client with per-project subscription. Matches the api-side
// ws-broadcast contract (subscribe/unsubscribe/ping commands, scoped events).

import type { WsClientCommand, WsEvent } from '@ladd-screenshot/shared';
import { createSignal } from 'solid-js';

type Listener = (event: WsEvent) => void;

export type WsConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

const [wsStatus, setWsStatus] = createSignal<WsConnectionStatus>('idle');
export const wsConnectionStatusSignal = wsStatus;

let socket: WebSocket | null = null;
const listeners = new Set<Listener>();
const subscriptions = new Set<string>();
let reconnectTimer: number | null = null;

function endpoint(): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.host}/ws`;
}

function send(cmd: WsClientCommand): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(cmd));
  }
}

export function connectWs(): void {
  if (socket && socket.readyState !== WebSocket.CLOSED) return;
  setWsStatus(socket ? 'reconnecting' : 'connecting');
  socket = new WebSocket(endpoint());
  socket.addEventListener('open', () => {
    setWsStatus('connected');
    for (const projectId of subscriptions) send({ type: 'subscribe', projectId });
  });
  socket.addEventListener('message', (msg) => {
    try {
      const evt = JSON.parse(msg.data) as WsEvent;
      for (const fn of listeners) fn(evt);
    } catch {
      // ignore malformed payloads
    }
  });
  socket.addEventListener('error', () => {
    setWsStatus('disconnected');
  });
  socket.addEventListener('close', () => {
    setWsStatus('reconnecting');
    if (reconnectTimer) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connectWs();
    }, 2000);
  });
}

export function subscribeProject(projectId: string): void {
  subscriptions.add(projectId);
  send({ type: 'subscribe', projectId });
}

export function unsubscribeProject(projectId: string): void {
  subscriptions.delete(projectId);
  send({ type: 'unsubscribe', projectId });
}

export function onWsEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
